import { Trophy, Vault, Sparkles, X, ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * VictoryModal — celebratory modal shown immediately after a successful Defeat.
 * Offers a one-tap "Yes, fund my Vault" CTA that diverts the freed monthly_cost
 * into the Hero Vault (mocked transfer).
 *
 * Props:
 *   - open: boolean
 *   - onOpenChange(next): close handler
 *   - sub: { id, name, monthly_cost } — defeated subscription
 *   - onFundVault(): async — diverts to vault
 *   - onSkip(): just dismisses
 *   - busy: boolean — true while divert request is in flight
 */
export default function VictoryModal({ open, onOpenChange, sub, onFundVault, onSkip, busy }) {
  const amount = Number(sub?.monthly_cost || 0);
  const annual = amount * 12;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-3xl border-2 border-amber-200 p-0 overflow-hidden"
        data-testid="victory-modal"
      >
        <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white px-6 pt-7 pb-6 relative">
          <div className="absolute -top-10 -right-10 w-44 h-44 bg-amber-400/30 rounded-full blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-12 -left-10 w-40 h-40 bg-yellow-300/15 rounded-full blur-3xl" aria-hidden="true" />
          <div className="relative flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-amber-400 text-slate-900 flex items-center justify-center ring-4 ring-amber-400/20 shadow-md">
              <Trophy className="w-7 h-7" strokeWidth={2.6} />
            </div>
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-black uppercase tracking-[0.18em]">
                <Sparkles className="w-3 h-3" strokeWidth={3} />
                Victory!
              </span>
              <DialogTitle
                className="text-2xl font-black tracking-tight text-white mt-1"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {sub?.name ? `${sub.name} defeated.` : "Bill defeated."}
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-sm font-medium mt-0.5">
                You just freed up{" "}
                <span className="font-black text-amber-300" data-testid="victory-amount">
                  ${amount.toFixed(2)}/month
                </span>
                .
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 bg-white">
          <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center flex-shrink-0 shadow-sm">
                <Vault className="w-5 h-5" strokeWidth={2.6} />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-base font-black text-slate-900 leading-tight"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  Auto-divert to Hero Vault?
                </p>
                <p className="text-[12.5px] leading-snug text-slate-700 font-medium mt-1">
                  Send the{" "}
                  <span className="font-black text-slate-900">${amount.toFixed(2)}</span>{" "}
                  you just reclaimed straight into your Hero Vault. That's{" "}
                  <span className="font-black text-emerald-700">${annual.toFixed(2)}/yr</span>{" "}
                  growing on autopilot.
                </p>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.16em] mt-2">
                  Prototype · transfer is simulated
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 pb-6 pt-0 flex flex-col-reverse sm:flex-col-reverse gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onSkip}
            disabled={busy}
            className="w-full h-11 rounded-2xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-bold"
            data-testid="victory-skip-button"
          >
            <X className="w-4 h-4 mr-2" strokeWidth={2.6} />
            No thanks
          </Button>
          <Button
            type="button"
            onClick={onFundVault}
            disabled={busy}
            className="w-full h-12 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-900 font-black tracking-wide gap-2 disabled:opacity-70"
            data-testid="victory-fund-button"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Vault className="w-4 h-4" strokeWidth={2.8} />}
            Yes, fund my Vault
            <ArrowRight className="w-4 h-4" strokeWidth={2.8} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
